import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { PageHeader, PageSkeleton, Tabs, type TabDef } from "@/components/ui";
import { TemplatesTab } from "@/components/test-program/templates-tab";
import { CadenceTab } from "@/components/test-program/cadence-tab";
import { SchedulesTab } from "@/components/test-program/schedules-tab";

type TestProgramTab = "templates" | "cadence" | "schedules";

const ALLOWED_TABS: TestProgramTab[] = ["templates", "cadence", "schedules"];

interface TestProgramSearch {
  tab?: TestProgramTab;
}

function parseTab(value: unknown): TestProgramTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/test-program/")({
  component: TestProgramPage,
  validateSearch: (search: Record<string, unknown>): TestProgramSearch => ({
    tab: parseTab(search.tab),
  }),
});

function TestProgramPage() {
  const { t } = useTranslation("nav");
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { isOrgAdmin, isLoading: permissionsLoading } = usePermissions();

  const activeTab: TestProgramTab = tab ?? "templates";

  const setActiveTab = (next: TestProgramTab) => {
    void navigate({
      to: "/test-program",
      // Templates is the default, so it clears the param instead of stacking a
      // history entry for every tab switch.
      search: next === "templates" ? {} : { tab: next },
      replace: true,
    });
  };

  // `usePermissions()` reads deny-all until its envelope resolves, so the
  // redirect below has to wait for a real answer — deciding earlier bounces
  // every hard navigation (bookmark, deep link, reload) to the home route.
  if (permissionsLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  // All three configuration surfaces are org-admin only; the panels keep their
  // own action-level `can(...)` gates on top.
  if (!isOrgAdmin) {
    void navigate({ to: "/" });
    return null;
  }

  const tabs: TabDef<TestProgramTab>[] = [
    { value: "templates", label: t("testProgram.templates", { ns: "nav" }) },
    { value: "cadence", label: t("testProgram.cadence", { ns: "nav" }) },
    { value: "schedules", label: t("testProgram.schedules", { ns: "nav" }) },
  ];

  return (
    <div className="p-8">
      <div className="space-y-4">
        <PageHeader title={t("testProgram.title", { ns: "nav" })} />

        <div>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ariaLabel={t("testProgram.title", { ns: "nav" })}
          />

          <div
            className="mt-6"
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {activeTab === "templates" && <TemplatesTab />}
            {activeTab === "cadence" && <CadenceTab />}
            {activeTab === "schedules" && <SchedulesTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
